import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import StatusPill, { STATUS_ORDER, STATUS_LABELS } from '../../components/StatusPill';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Plus, Building2, Trash2, LayoutList, KanbanSquare, Tag, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import SortHeader from '../../components/SortHeader';
import BulkDeleteDialog from '../../components/BulkDeleteDialog';
import { useTableTools, useSortedItems } from '../../components/tableTools';

// ── Funnel column config ──────────────────────────────────────────────────────
const FUNNEL_COLS = [
    { key: 'lead',           label: 'Lead',           color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
    { key: 'discovery',      label: 'Discovery',      color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
    { key: 'proposal_draft', label: 'Proposal Draft', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
    { key: 'proposal_sent',  label: 'Proposal Sent',  color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
    { key: 'active',         label: 'Active',         color: '#10b981', bg: '#f0fdf4', border: '#a7f3d0' },
    { key: 'paused',         label: 'Paused',         color: '#f97316', bg: '#fff7ed', border: '#fed7aa' },
    { key: 'lost',           label: 'Lost',           color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
];

const SORT_ACCESSORS = {
    company_name: (c) => (c.company_name || '').toLowerCase(),
    main_contact_name: (c) => (c.main_contact_name || '').toLowerCase(),
    status: (c) => c.status || '',
    owner_name: (c) => (c.owner_name || '').toLowerCase(),
    industry: (c) => (c.industry || '').toLowerCase(),
};

// ── Avatar initials helper ────────────────────────────────────────────────────
function Avatar({ name, color }) {
    const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    return (
        <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-[11px] shrink-0"
            style={{ background: color }}
        >
            {initials}
        </div>
    );
}

// ── Kanban card ───────────────────────────────────────────────────────────────
function FunnelCard({ client, index, colColor, navigate }) {
    return (
        <Draggable draggableId={client.id} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={() => navigate(`/clients/${client.id}`)}
                    data-testid={`funnel-card-${client.id}`}
                    className="bg-white rounded-lg border cursor-pointer transition-all duration-150 p-3 group"
                    style={{
                        borderColor: snapshot.isDragging ? colColor : '#e5e7eb',
                        boxShadow: snapshot.isDragging
                            ? `0 8px 24px rgba(0,0,0,0.12), 0 0 0 2px ${colColor}40`
                            : '0 1px 3px rgba(0,0,0,0.05)',
                        ...provided.draggableProps.style,
                    }}
                >
                    <div className="flex items-start gap-2.5">
                        <Avatar name={client.company_name} color={colColor} />
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[13px] text-gray-900 truncate group-hover:text-[#0B3C5D] transition-colors">
                                {client.company_name}
                            </div>
                            {client.trading_name && client.trading_name !== client.company_name && (
                                <div className="text-[11px] text-gray-400 truncate">{client.trading_name}</div>
                            )}
                        </div>
                    </div>
                    {client.main_contact_name && (
                        <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-gray-500">
                            <User size={10} className="shrink-0" />
                            <span className="truncate">{client.main_contact_name}</span>
                        </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                        {client.industry && (
                            <span className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                                {client.industry}
                            </span>
                        )}
                        {client.source && (
                            <span className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                <Tag size={8} />{client.source}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </Draggable>
    );
}

// ── Funnel column ─────────────────────────────────────────────────────────────
function FunnelColumn({ col, cards, navigate }) {
    return (
        <div className="flex flex-col min-w-[220px] w-[220px] shrink-0" data-testid={`funnel-col-${col.key}`}>
            {/* Column header */}
            <div
                className="flex items-center justify-between px-3 py-2.5 rounded-t-xl border border-b-0"
                style={{ background: col.bg, borderColor: col.border }}
            >
                <div className="flex items-center gap-2">
                    <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: col.color }}
                    />
                    <span
                        className="text-[11px] font-semibold uppercase tracking-wide"
                        style={{ color: col.color }}
                    >
                        {col.label}
                    </span>
                </div>
                <span
                    className="text-[11px] font-bold px-1.5 py-0.5 rounded-md"
                    style={{ background: col.color + '18', color: col.color }}
                >
                    {cards.length}
                </span>
            </div>

            {/* Drop zone */}
            <Droppable droppableId={col.key}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex-1 rounded-b-xl border border-t-0 p-2 space-y-2 transition-colors duration-150"
                        style={{
                            minHeight: 80,
                            borderColor: col.border,
                            background: snapshot.isDraggingOver
                                ? col.color + '0e'
                                : col.bg,
                        }}
                    >
                        {cards.map((c, i) => (
                            <FunnelCard key={c.id} client={c} index={i} colColor={col.color} navigate={navigate} />
                        ))}
                        {provided.placeholder}
                        {cards.length === 0 && !snapshot.isDraggingOver && (
                            <div className="text-center py-6 text-[11px] text-gray-300 select-none">
                                No clients
                            </div>
                        )}
                    </div>
                )}
            </Droppable>
        </div>
    );
}

export default function ClientsList() {
    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();
    const { user, can } = useAuth();
    const isAdmin = user?.role === 'admin';
    const canCreate = can('crm', 'create');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [view, setView] = useState('board'); // 'list' | 'board'
    const [showNew, setShowNew] = useState(sp.get('new') === '1');
    const { selected, toggleOne, toggleAll, clear, sortKey, sortDir, changeSort } = useTableTools({ defaultSortKey: 'company_name' });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const sortedItems = useSortedItems(items, sortKey, sortDir, SORT_ACCESSORS);

    const fetchClients = useCallback(async () => {
        setLoading(true);
        const params = { per_page: 200 };
        if (search) params.search = search;
        if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
        try {
            const r = await api.get('/clients', { params });
            setItems(r.data.items);
            clear();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, clear]);

    useEffect(() => { fetchClients(); }, [fetchClients]);

    // ── Drag-and-drop: move client to new status column ───────────────────────
    const onDragEnd = useCallback(async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const newStatus = destination.droppableId;
        // Optimistic update
        setItems(prev => prev.map(c => c.id === draggableId ? { ...c, status: newStatus } : c));
        try {
            await api.patch(`/clients/${draggableId}`, { status: newStatus });
        } catch (e) {
            toast.error('Failed to update status');
            fetchClients(); // revert
        }
    }, [fetchClients]);

    const handleCreated = (newClient) => {
        setShowNew(false);
        sp.delete('new'); setSp(sp);
        toast.success(`${newClient.company_name} created`);
        navigate(`/clients/${newClient.id}`);
    };

    const openDeleteDialog = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        try {
            const r = await api.post('/clients/bulk-delete-preview', { ids });
            setConfirmDelete({ counts: r.data.counts, names: r.data.sample_names, ids });
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to preview');
        }
    };

    const doDelete = async (ids) => {
        try {
            const r = await api.post('/clients/bulk-delete', { ids });
            const d = r.data.deleted || {};
            toast.success(`Deleted ${d.clients || 0} client(s) + ${(d.proposals || 0) + (d.contracts || 0) + (d.invoices || 0)} linked records`);
            fetchClients();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Delete failed');
        }
    };

    // Build funnel columns from items (exclude 'archived')
    const funnelCols = FUNNEL_COLS.map(col => ({
        ...col,
        cards: items.filter(c => c.status === col.key),
    }));

    return (
        <div data-testid="clients-list-page" className="bm-fade-in">
            <PageHeader
                title="Clients"
                description="All Beyond Measure clients — leads, active accounts, paused, archived."
                actions={
                    <div className="flex gap-2">
                        {isAdmin && selected.size > 0 && view === 'list' && (
                            <Button
                                onClick={openDeleteDialog}
                                variant="outline"
                                className="h-9 text-red-600 border-red-200 hover:bg-red-50"
                                data-testid="clients-bulk-delete-btn"
                            >
                                <Trash2 size={15} className="mr-1.5" /> Delete {selected.size} selected
                            </Button>
                        )}
                        {canCreate && (
                            <Button
                                onClick={() => setShowNew(true)}
                                className="h-9"
                                style={{ background: 'var(--bm-primary)' }}
                                data-testid="clients-new-btn"
                            >
                                <Plus size={16} className="mr-1" /> New client
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[240px] max-w-sm">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--bm-text-subtle)]" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by company, contact, email…"
                            className="pl-9 h-9 bg-white"
                            data-testid="clients-search-input"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 w-[180px] bg-white" data-testid="clients-status-filter">
                            <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUS_ORDER.map(s => (
                                <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {/* View toggle */}
                    <div className="flex items-center border border-[color:var(--bm-border)] rounded-lg overflow-hidden bg-white h-9" data-testid="clients-view-toggle">
                        <button
                            onClick={() => setView('board')}
                            title="Board view"
                            data-testid="clients-view-board"
                            className={`flex items-center gap-1.5 px-3 h-full text-[12.5px] font-medium transition ${view === 'board' ? 'bg-[color:var(--bm-primary)] text-white' : 'text-[color:var(--bm-text-muted)] hover:bg-[color:var(--bm-bg-mute)]'}`}
                        >
                            <KanbanSquare size={14} /> Board
                        </button>
                        <button
                            onClick={() => setView('list')}
                            title="List view"
                            data-testid="clients-view-list"
                            className={`flex items-center gap-1.5 px-3 h-full text-[12.5px] font-medium transition border-l border-[color:var(--bm-border)] ${view === 'list' ? 'bg-[color:var(--bm-primary)] text-white' : 'text-[color:var(--bm-text-muted)] hover:bg-[color:var(--bm-bg-mute)]'}`}
                        >
                            <LayoutList size={14} /> List
                        </button>
                    </div>
                </div>
            </PageHeader>

            {/* ── BOARD VIEW ── */}
            {view === 'board' && (
                <div className="px-8 py-6">
                    {loading ? (
                        <div className="text-center py-16 text-[color:var(--bm-text-muted)] text-[13px]">Loading…</div>
                    ) : (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <div className="flex gap-3 overflow-x-auto pb-4" data-testid="funnel-board">
                                {funnelCols.map(col => (
                                    <FunnelColumn key={col.key} col={col} cards={col.cards} navigate={navigate} />
                                ))}
                            </div>
                        </DragDropContext>
                    )}
                </div>
            )}

            {/* ── LIST VIEW ── */}
            {view === 'list' && (
                <div className="px-8 py-6">
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                        <table className="w-full text-[13px]">
                            <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                                <tr>
                                    {isAdmin && (
                                        <th className="w-10 px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={items.length > 0 && selected.size === items.length}
                                                onChange={() => toggleAll(items)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="cursor-pointer"
                                                data-testid="clients-select-all"
                                                aria-label="Select all clients"
                                            />
                                        </th>
                                    )}
                                    <SortHeader label="Company"  sortKey="company_name"      currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                    <SortHeader label="Contact"  sortKey="main_contact_name" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                    <SortHeader label="Status"   sortKey="status"            currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                    <SortHeader label="Owner"    sortKey="owner_name"        currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                    <SortHeader label="Industry" sortKey="industry"          currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={isAdmin ? 6 : 5} className="px-5 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>
                                )}
                                {!loading && sortedItems.length === 0 && (
                                    <tr>
                                        <td colSpan={isAdmin ? 6 : 5} className="px-5 py-14 text-center">
                                            <Building2 size={36} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                                            <div className="mt-3 text-[color:var(--bm-text-muted)] text-[14px]">No clients yet.</div>
                                            <div className="text-[12.5px] text-[color:var(--bm-text-subtle)] mt-1">Add your first client to get started.</div>
                                            {canCreate && (
                                                <Button
                                                    onClick={() => setShowNew(true)}
                                                    className="mt-4 h-9"
                                                    style={{ background: 'var(--bm-primary)' }}
                                                    data-testid="clients-empty-new-btn"
                                                >
                                                    <Plus size={16} className="mr-1" /> New client
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                {sortedItems.map((c) => {
                                    const isSel = selected.has(c.id);
                                    return (
                                        <tr
                                            key={c.id}
                                            onClick={() => navigate(`/clients/${c.id}`)}
                                            className={`border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer transition ${isSel ? 'bg-[color:var(--bm-accent-50)]/60' : ''}`}
                                            data-testid={`client-row-${c.id}`}
                                        >
                                            {isAdmin && (
                                                <td className="px-4 py-3.5" onClick={(e) => { e.stopPropagation(); toggleOne(c.id); }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSel}
                                                        onChange={() => toggleOne(c.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="cursor-pointer"
                                                        data-testid={`client-select-${c.id}`}
                                                        aria-label={`Select ${c.company_name}`}
                                                    />
                                                </td>
                                            )}
                                            <td className="px-5 py-3.5">
                                                <div className="font-medium text-[color:var(--bm-text)]">{c.company_name}</div>
                                                {c.trading_name && (
                                                    <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">{c.trading_name}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">
                                                {c.main_contact_name || <span className="text-[color:var(--bm-text-subtle)]">—</span>}
                                                {c.email && <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">{c.email}</div>}
                                            </td>
                                            <td className="px-4 py-3.5"><StatusPill status={c.status} /></td>
                                            <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{c.owner_name || '—'}</td>
                                            <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{c.industry || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <NewClientDialog open={showNew} onClose={() => { setShowNew(false); sp.delete('new'); setSp(sp); }} onCreated={handleCreated} />

            <BulkDeleteDialog
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                info={confirmDelete}
                entityLabel="client"
                onConfirm={doDelete}
                testId="clients-bulk-delete-dialog"
            />
        </div>
    );
}

function NewClientDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState({
        company_name: '', main_contact_name: '', email: '', phone: '',
        industry: '', status: 'lead', source: '', notes: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const reset = () => setForm({ company_name: '', main_contact_name: '', email: '', phone: '', industry: '', status: 'lead', source: '', notes: '' });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.company_name.trim()) return toast.error('Company name is required');
        setSubmitting(true);
        try {
            const body = { ...form };
            // Strip blank email to avoid Pydantic EmailStr validation error
            if (!body.email) delete body.email;
            const r = await api.post('/clients', body);
            onCreated(r.data);
            reset();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[560px]" data-testid="new-client-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-[20px]">New client</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">
                        Create a client record. You can add contacts, proposals and contracts from the detail page.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                            <Label className="text-[12.5px] font-medium">Company name *</Label>
                            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5 h-10" required data-testid="new-client-company-input" />
                        </div>
                        <div>
                            <Label className="text-[12.5px] font-medium">Main contact</Label>
                            <Input value={form.main_contact_name} onChange={(e) => setForm({ ...form, main_contact_name: e.target.value })} className="mt-1.5 h-10" data-testid="new-client-contact-input" />
                        </div>
                        <div>
                            <Label className="text-[12.5px] font-medium">Email</Label>
                            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-10" data-testid="new-client-email-input" />
                        </div>
                        <div>
                            <Label className="text-[12.5px] font-medium">Phone</Label>
                            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-10" />
                        </div>
                        <div>
                            <Label className="text-[12.5px] font-medium">Industry</Label>
                            <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-1.5 h-10" />
                        </div>
                        <div>
                            <Label className="text-[12.5px] font-medium">Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger className="mt-1.5 h-10" data-testid="new-client-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[12.5px] font-medium">Lead source</Label>
                            <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1.5 h-10" placeholder="Referral, LinkedIn, etc." />
                        </div>
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={submitting} style={{ background: 'var(--bm-primary)' }} data-testid="new-client-submit-btn">
                            {submitting ? 'Creating…' : 'Create client'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
